import classNames from 'classnames'
import { EditPencil as EditPencilIcon, Plus as PlusIcon } from 'iconoir-react'
import { ReactElement, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import { ScrollableList } from '../analytics/ScrollableList'
import { DraggableContact } from '../contacts/DraggableContact'
import { ItemTypes } from '../sortable-dnd/ItemTypes'
import { Tooltip } from '../Tooltip'
import { ContactsPopover } from './ContactsPopover'
import EditPipeline from './EditPipeline'
import { isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import type { ContactJson } from 'app/types/entities'
import type { StageType } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import type { ListCardConfig } from 'lib/listCardConfig'
import type { TaskCountsMap } from 'app/hooks/usePipelineTaskCounts'
import type { CommContextMap } from 'app/hooks/usePipelineCommContext'

export function StageColumn({
  index,
  stage,
  stageContacts,
  addContactToPipeline,
  contactHandler,
  updatePipelineStage,
  deleteStage,
  updateContactStage,
  removeContact,
  config,
  taskCounts,
  commContext,
}: {
  index: number
  stage: { id: string } & StageType
  stageContacts: ContactJson[]
  addContactToPipeline: (contact: ContactJson, stage: StageType) => void
  contactHandler: (contactId: string) => void
  updatePipelineStage: (stage: StageType, index: number) => Promise<void | { error?: string }>
  deleteStage: (name: string) => void
  updateContactStage: (contactId: string, stage: StageType) => void
  removeContact: (contactId: string) => void
  config: ListCardConfig
  taskCounts: TaskCountsMap
  commContext: CommContextMap
}): ReactElement {
  const contactsRef = useRef<HTMLDivElement>(null)
  const [tooltipContent, setTooltipContent] = useState<string | undefined>()
  // `stage.id` carries the pipeline's own id (see PipelineLayout's `{ id: pipeline.id!, ...stage }`),
  // not the stage's. A system pipeline's stage name/order/removal are the Action Center boards'
  // structure; only colour is safe to edit here (see updatePipelineStage/deleteStage guards).
  const structureLocked = isSystemPipelineId(stage.id)
  const moveStage = (contact: ContactJson, stage) => {
    if (contact.pipelines?.find((pipeline) => pipeline.stage === stage.name)) return
    updateContactStage(contact.id!, stage)
  }
  const [{ isOver, isSame }, drop] = useDrop(
    () => ({
      accept: ItemTypes.CONTACT,
      drop: (contact: ContactJson) => moveStage(contact, stage),
      collect: (monitor) => {
        return {
          isOver: !!monitor.isOver(),
          isSame: !!monitor.getItem()?.pipelines?.find((pipeline) => pipeline.stage === stage.name),
        }
      },
    }),
    [stage],
  )

  return (
    <div
      ref={(element) => {
        drop(element)
      }}
      className={classNames(
        `relative flex h-full w-72 shrink-0 snap-start flex-col gap-y-3 overflow-hidden rounded-lg px-2 pt-2`,
        {
          [`bg-blue-400/[0.08]`]: isOver && !isSame,
          [`bg-surface-subtle`]: isSame,
        },
      )}
    >
      <div className="flex shrink-0 justify-between overflow-hidden rounded-lg border border-subtle bg-surface align-middle">
        <div
          className={`inline-flex w-full items-center justify-start self-stretch border-b-4 border-${stage.color || `strong`} px-3 pb-2 pt-2`}
        >
          <div className="flex w-4/5 items-center gap-x-3 bg-surface">
            <Tooltip
              className="shrink cursor-pointer truncate"
              message={tooltipContent}
              placement="top"
              onMouseOver={(evt) => {
                if (
                  evt.currentTarget instanceof HTMLElement &&
                  evt.currentTarget.scrollWidth > evt.currentTarget.clientWidth
                ) {
                  setTooltipContent(stage.name)
                } else {
                  setTooltipContent(undefined)
                }
              }}
            >
              <span className="text-heading-xs text-strong">{stage.name}</span>
            </Tooltip>
            <div className="border-default flex h-5 items-center justify-center rounded border-[0.5px] bg-gray-minimal px-1.5 pt-px text-label-xs leading-none text-gray-strong">
              {stageContacts.length}
            </div>
          </div>
          <div className="flex shrink-0 place-items-end space-x-3">
            <Tooltip message="Add contact" placement="top">
              <PlusIcon
                className="h-5 w-5 cursor-pointer text-minimal"
                onClick={() => contactsRef.current?.click?.()}
              />
            </Tooltip>
            <Tooltip message="Edit stage" placement="top">
              <EditPencilIcon
                className="h-5 w-4 cursor-pointer text-minimal"
                onClick={() =>
                  showModal(
                    <EditPipeline
                      index={index}
                      stage={stage}
                      updatePipelineStage={updatePipelineStage}
                      deleteStage={() => deleteStage(stage.name)}
                      type="UPDATE"
                      structureLocked={structureLocked}
                    />,
                    {
                      title: `Edit Stage`,
                      isFullScreen: false,
                      removePadding: true,
                      useRadix: true,
                    },
                  )
                }
              />
            </Tooltip>
          </div>
        </div>
      </div>
      {(!isOver || isSame) && (
        <ContactsPopover
          ref={contactsRef}
          pipelineId={stage.id}
          hideButton={stageContacts.length > 0}
          contactHandler={contactHandler}
          selectedContact={(contact) => addContactToPipeline(contact, stage)}
        />
      )}
      <ScrollableList fill innerClassName="gap-y-2" className="relative -top-3">
        {(!isOver || isSame) &&
          stageContacts.length > 0 &&
          stageContacts
            .sort((a, b) => {
              if (a.name && b.name) return a.name.localeCompare(b.name)
              return 0
            })
            .map((contact) => (
              <DraggableContact
                key={contact.id}
                contact={contact as ContactJson}
                openContactDrawer={contactHandler}
                removeContact={removeContact}
                config={config}
                taskCounts={taskCounts[contact.id!]}
                commContext={commContext[contact.id!]}
              />
            ))}
      </ScrollableList>
    </div>
  )
}
